import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { FaMoon, FaSun } from 'react-icons/fa';
import styles from './display-settings.module.scss';
import { ReactComponent as ParamsIcon } from '../../assets/images/params.svg';

/** Pages inscription / confirmation : bouton contrasté (noir en clair, blanc en sombre). */
type SettingsBtnVariant = 'onBlue' | 'onWhite';
type SettingsBtnPlacement = 'fixed' | 'inline';

function getSettingsBtnVariant(pathname: string): SettingsBtnVariant {
  // Fond bleu (gradient) -> bouton blanc même en light mode.
  if (pathname === '/' || pathname === '/login' || pathname === '/register-types') return 'onBlue';
  if (pathname.startsWith('/confirm') || pathname.startsWith('/confrim')) return 'onBlue';
  if (pathname.startsWith('/modifier-inscription')) return 'onBlue';
  if (pathname.startsWith('/recuperer-badge')) return 'onBlue';

  // Pages avec gros encarts clairs -> bouton noir en light mode.
  if (pathname.startsWith('/register')) return 'onWhite';

  return 'onBlue';
}

const STORAGE_THEME = 'salon-waiting-theme';
const STORAGE_ZOOM = 'salon-waiting-zoom';
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 2;

const clampZoom = (value: number) => {
  const roundedValue = Math.round(value * 10) / 10;
  return Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, roundedValue));
};

const DisplaySettings = ({
  placement = 'fixed',
}: {
  placement?: SettingsBtnPlacement,
}) => {
  const { pathname } = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [theme, setTheme] = useState<'light' | 'dark'>('light');
  const [zoom, setZoom] = useState(1);
  const btnVariant = useMemo(() => getSettingsBtnVariant(pathname), [pathname]);

  const shouldRenderButton = !(placement === 'fixed' && pathname.startsWith('/register'));

  useEffect(() => {
    if (!shouldRenderButton) {
      setIsOpen(false);
    }
  }, [shouldRenderButton]);

  useEffect(() => {
    let savedTheme = 'light';
    let savedZoom = 1;

    try {
      const localTheme = localStorage.getItem(STORAGE_THEME);
      const localZoom = localStorage.getItem(STORAGE_ZOOM);
      if (localTheme === 'dark') savedTheme = 'dark';
      if (localZoom) savedZoom = clampZoom(Number(localZoom));
    } catch (error) {
      // no-op
    }

    setTheme(savedTheme as 'light' | 'dark');
    setZoom(savedZoom);
  }, []);

  useEffect(() => {
    document.body.classList.toggle('dark-mode', theme === 'dark');
    try {
      localStorage.setItem(STORAGE_THEME, theme);
    } catch (error) {
      // no-op
    }
  }, [theme]);

  useEffect(() => {
    document.documentElement.style.setProperty('--zoom', String(zoom));
    try {
      localStorage.setItem(STORAGE_ZOOM, String(zoom));
    } catch (error) {
      // no-op
    }
  }, [zoom]);

  useEffect(() => {
    const onEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      window.addEventListener('keydown', onEsc);
    }

    return () => {
      window.removeEventListener('keydown', onEsc);
    };
  }, [isOpen]);

  const zoomValue = useMemo(() => zoom.toFixed(1).replace('.0', ''), [zoom]);

  return (
    <>
      {shouldRenderButton && (
        <button
          type="button"
          className={[
            styles.settingsBtn,
            placement === 'fixed' ? styles.settingsBtnFixed : styles.settingsBtnInline,
            btnVariant === 'onWhite' ? styles.settingsBtnOnWhite : '',
          ].join(' ')}
          onClick={() => setIsOpen(true)}
          aria-label="Paramètres d'affichage"
        >
          <ParamsIcon className={styles.settingsBtnIcon} width={20} height={20} aria-hidden />
          Paramètre d&apos;affichage
        </button>
      )}

      {shouldRenderButton && isOpen && (
        <div
          className={styles.popupOverlay}
          role="presentation"
          onClick={(event) => {
            if (event.target === event.currentTarget) {
              setIsOpen(false);
            }
          }}
        >
          <div
            className={styles.popup}
            role="dialog"
            aria-labelledby="display-settings-title"
            aria-modal="true"
          >
            <div className={styles.popupHeader}>
              <button
                type="button"
                className={styles.popupClose}
                onClick={() => setIsOpen(false)}
              >
                Fermer
              </button>
            </div>
            <h2 className={styles.popupTitle} id="display-settings-title">
              Parametres d&apos;accessibilite
            </h2>
            <hr className={styles.popupSeparator} />

            <div className={styles.modeOptions}>
              <label className={styles.modeOption}>
                <input
                  type="radio"
                  name="display-mode"
                  value="light"
                  checked={theme === 'light'}
                  onChange={() => setTheme('light')}
                />
                <FaSun className={styles.modeIcon} />
                Mode clair
              </label>
              <label className={styles.modeOption}>
                <input
                  type="radio"
                  name="display-mode"
                  value="dark"
                  checked={theme === 'dark'}
                  onChange={() => setTheme('dark')}
                />
                <FaMoon className={styles.modeIcon} />
                Mode sombre
              </label>
            </div>

            <p className={styles.popupSectionLabel}>Zoom</p>
            <div className={styles.zoomControls}>
              <button
                type="button"
                className={styles.zoomBtn}
                aria-label="Reduire le zoom"
                onClick={() => setZoom((value) => clampZoom(value - 0.1))}
              >
                -
              </button>
              <span className={styles.zoomValue}>{zoomValue}</span>
              <button
                type="button"
                className={styles.zoomBtn}
                aria-label="Augmenter le zoom"
                onClick={() => setZoom((value) => clampZoom(value + 0.1))}
              >
                +
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DisplaySettings;
